import {
  CaseSizeRuleListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import { pluralize } from "@sellar-io/common";
import { useMinimumOrderQuantityRequirement } from "~/hooks/usePriceTierOrderRequirements";
import { generatePriceTierOrderRuleDescription } from "../../../utils/sellarHelpers";
import { Icon } from "@sellar-io/components";
import { ProgressBarIndigo } from "../../progressBar/ProgressBar";

type OrderItem = {
  quantity: number;
  containerType: string | null;
  volume: number | null;
  unit: string | null;
  packQuantity: number | null;
};

export function MinimumOrderQuantityProgress({
  orderItemList,
  rule,
  hideWhenMet,
  hideIfNotApplicable,
  caseSizeRuleList,
}: {
  orderItemList: OrderItem[];
  rule: SupplierGetResponseType["CustomerZones"][number]["PriceTier"]["PriceTierOrderRules"][number];
  hideWhenMet?: boolean;
  hideIfNotApplicable?: boolean;
  caseSizeRuleList: Pick<
    CaseSizeRuleListResponseType[number],
    "quantity" | "containerType" | "volume" | "unit"
  >[];
}) {
  const {
    isApplicable: isOrderRuleApplicable,
    isMet: isOrderRuleMet,
    percentageComplete,
    remaining,
    itemTypeName,
  } = useMinimumOrderQuantityRequirement({
    rule,
    orderItemList,
    caseSizeRuleList,
  });

  const generateProgressText = () => {
    const amountLeft = remaining;

    if (rule.itemType === "any") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>
          {pluralize("item", amountLeft)}. Minimum of {rule.quantity} per order
        </div>
      );
    }

    if (rule.itemType === "can") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>
          {`${pluralize("case", amountLeft)} of cans`}. Minimum of{" "}
          {rule.quantity} per order
        </div>
      );
    }

    if (rule.itemType === "bottle") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>
          {`${pluralize("case", amountLeft)} of bottles`}. Minimum of{" "}
          {rule.quantity} per order
        </div>
      );
    }

    if (rule.itemType === "draught") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span>{" "}
          {pluralize("draught item", amountLeft)}. Minimum of {rule.quantity}{" "}
          per order
        </div>
      );
    }

    if (rule.itemType === "bagInBox") {
      return (
        <div className="text-indigo-800">
          Add <span className="font-bold">{amountLeft} more </span> bag in{" "}
          {pluralize("box", amountLeft)}. Minimum of {rule.quantity} per order
        </div>
      );
    }

    return (
      <div className="text-indigo-800">
        Add <span className="font-bold">{amountLeft} more </span>{" "}
        {pluralize(rule.itemType, amountLeft)}
        {rule.unit && rule.volume ? ` over ${rule.volume}${rule.unit}` : ""}.
        Minimum of {rule.quantity} per order
      </div>
    );
  };

  if (hideWhenMet && isOrderRuleMet) {
    return null;
  }

  if (hideIfNotApplicable && !isOrderRuleApplicable) {
    return null;
  }

  if (!isOrderRuleApplicable) {
    return <div>{generatePriceTierOrderRuleDescription(rule)}</div>;
  }

  if (isOrderRuleMet) {
    return (
      <div className={"flex gap-1 text-indigo-800"}>
        <Icon icon={"CHECK_CIRCLE"} />
        <strong>
          Minimum order of {rule.quantity} {itemTypeName} met!
        </strong>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {generateProgressText()}
      <ProgressBarIndigo percentage={percentageComplete} />
    </div>
  );
}
